<template>
  <div class="lens-imaging-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$1/u + 1/v = 1/f$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 500 220">
           <!-- 光轴 -->
           <line x1="0" y1="110" x2="500" y2="110" stroke="rgba(255,255,255,0.15)" stroke-width="1.5" stroke-dasharray="4,2" />
           
           <!-- 透镜 (凸透镜) -->
           <path d="M 250 30 Q 275 110 250 190 Q 225 110 250 30" fill="rgba(6, 182, 212, 0.2)" stroke="#22d3ee" stroke-width="2" />
           <text x="250" y="22" text-anchor="middle" fill="#22d3ee" font-size="10" font-weight="700">凸透镜</text>

           <!-- 焦点 F -->
           <circle :cx="250 - f" cy="110" r="3" fill="#f59e0b" />
           <circle :cx="250 + f" cy="110" r="3" fill="#f59e0b" />
           <text :x="250 - f" y="125" text-anchor="middle" fill="#f59e0b" font-size="10" font-weight="700">F</text>
           <text :x="250 + f" y="125" text-anchor="middle" fill="#f59e0b" font-size="10" font-weight="700">F'</text>

           <!-- 物体 -->
           <g :transform="`translate(${250 - u}, 110)`">
              <line x1="0" y1="0" x2="0" y2="-40" stroke="#ef4444" stroke-width="3" />
              <path d="M -6 -34 L 0 -46 L 6 -34" fill="#ef4444" />
              <text y="18" text-anchor="middle" fill="#ef4444" font-size="11" font-weight="700">物体 u={{ u.toFixed(0) }}</text>
           </g>

           <!-- 像 - 修复倒立文字镜像问题 -->
           <g v-if="hasImage" :transform="`translate(${250 + v}, 110)`">
              <!-- 几何形状应用缩放 -->
              <g :transform="`scale(${magnification})`">
                 <line x1="0" y1="0" x2="0" y2="-40" :stroke="v > 0 ? '#10b981' : 'rgba(16, 185, 129, 0.4)'" :stroke-dasharray="v > 0 ? '0' : '4,2'" stroke-width="3" />
                 <path d="M -6 -34 L 0 -46 L 6 -34" :fill="v > 0 ? '#10b981' : 'rgba(16, 185, 129, 0.4)'" />
              </g>
              <!-- 文字独立于缩放，避免镜像倒立 -->
              <text :y="v > 0 ? Math.abs(magnification)*40 + 15 : -Math.abs(magnification)*40 - 10" 
                    text-anchor="middle" :fill="v > 0 ? '#10b981' : '#94a3b8'" font-size="11" font-weight="700">
                {{ v > 0 ? '实像' : '虚像' }} (v={{ Math.abs(v).toFixed(0) }})
              </text>
           </g>

           <!-- 关键光路 -->
           <g v-if="u > 0">
              <!-- 平行主轴支路 -->
              <line :x1="250 - u" y1="70" x2="250" y2="70" stroke="rgba(255,255,255,0.4)" stroke-width="1" />
              <line x1="250" y1="70" :x2="250 + (v > 0 ? v : 120)" :y2="70 + (110-70)/(f) * (v > 0 ? v : 120)" stroke="rgba(255,215,0,0.5)" stroke-width="1.5" />
           </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>成像性质</label>
              <span class="val">{{ imageDescription }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>物距 (u): {{ u.toFixed(0) }}</label>
                 <el-slider v-model="u" :min="10" :max="200" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>焦距 (f): {{ f.toFixed(0) }}</label>
                 <el-slider v-model="f" :min="20" :max="100" size="small"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>成像规律：</strong>u > 2f (缩小实像)；f &lt; u &lt; 2f (放大实像)；u &lt; f (放大正立虚像)。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LensImagingViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      u: 120,
      f: 50
    }
  },
  computed: {
    v() {
      if (Math.abs(this.u - this.f) < 2) return 1000; 
      return (this.f * this.u) / (this.u - this.f);
    },
    hasImage() { return Math.abs(this.u - this.f) > 3; },
    magnification() { return -this.v / this.u; },
    imageDescription() {
       if (this.u > this.f * 2) return "倒立、缩小的实像";
       if (Math.abs(this.u - this.f * 2) < 2) return "倒立、等大的实像";
       if (this.u > this.f) return "倒立、放大的实像";
       if (this.u < this.f) return "正立、放大的虚像";
       return "不成像 (平行光)";
    }
  }
}
</script>

<style scoped lang="scss">
.lens-imaging-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.8); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #64748b; font-weight: 700; } .val { font-size: 14px; color: #22d3ee; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: 2px; } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
